import React from 'react';
import { Alert, AlertTitle, Button, Stack } from '@mui/material';
import { Fingerprint } from '@mui/icons-material';

interface IProps {
  /**
   * Empfehlung des Backends: am Vorgang ist mindestens eine juristische Person
   * beteiligt.
   */
  isVisible: boolean;
  /** Namen der beteiligten juristischen Personen — macht den Vorschlag konkret. */
  legalPersonNameArray: string[];
  onCreate: () => void;
  onDismiss: () => void;
}

/**
 * Proaktive Empfehlung, einen GwG-Ordner anzulegen.
 *
 * DiNo erkennt die Konstellation selbst: eine juristische Person unter den
 * Beteiligten — gleich ob sie als Mandant am Vorgang hängt oder als Beteiligte
 * eines Rechtsgeschäfts. Der Ordner lässt sich jederzeit auch von Hand anlegen
 * (Datenablage → Ordner erstellen → Systemordner „GwG"); der Hinweis nimmt nur
 * die Arbeit ab, daran zu denken.
 *
 * Ein Grundstück war hier zunächst zusätzlich verlangt. Das war fachlich falsch:
 * die geldwäscherechtliche Prüfung hängt an der Beteiligung, nicht am
 * Kaufgegenstand — und die Abrufe fallen überwiegend bei gesellschafts-
 * rechtlichen Vorgängen an.
 *
 * Bewusst ein Banner und kein Dialog: ein aufpoppendes Fenster beim Öffnen des
 * Vorgangs wäre in dem Moment fast immer im Weg. Wer ablehnt, wird für diesen
 * Vorgang nicht erneut gefragt (`GwGSuggestionDismissed_at`).
 */
export const GwGSuggestionAlert: React.FC<IProps> = props => {
  if (!props.isVisible) {
    return null;
  }

  const nameText =
    props.legalPersonNameArray.length === 0
      ? ''
      : props.legalPersonNameArray.length === 1
        ? ` für ${props.legalPersonNameArray[0]}`
        : ` für ${props.legalPersonNameArray.slice(0, -1).join(', ')} und ${
            props.legalPersonNameArray[props.legalPersonNameArray.length - 1]
          }`;

  return (
    <Alert
      severity='info'
      icon={<Fingerprint fontSize='inherit' />}
      sx={{ mb: 2 }}
      action={
        <Stack direction='row' spacing={1}>
          <Button color='inherit' size='small' onClick={props.onDismiss}>
            Nicht erneut fragen
          </Button>
          <Button
            color='primary'
            variant='contained'
            size='small'
            onClick={props.onCreate}
          >
            GwG-Ordner anlegen
          </Button>
        </Stack>
      }
    >
      <AlertTitle>GwG-Ordner empfohlen</AlertTitle>
      An diesem Vorgang ist eine juristische Person beteiligt. Ein GwG-Ordner
      bündelt die geldwäscherechtlichen Unterlagen{nameText} und überwacht ihre
      Vollständigkeit.
    </Alert>
  );
};
